import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box, Container, Typography, Button, Divider, LinearProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    LockOutlined, NorthEastOutlined, SouthWestOutlined,
    ShowChartOutlined, AutoGraphOutlined, PieChartOutlined,
    TrendingUpOutlined, TrendingDownOutlined,
    SavingsOutlined, RepeatOutlined, BookmarkBorderOutlined, BarChartOutlined,
} from '@mui/icons-material';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { B, MUTUAL_FUNDS, NGX_STOCKS, PORTFOLIO } from './investData';

/* ─── Helpers ──────────────────────────────────────────────────── */
const fmt = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

const MiniChart: React.FC<{ data: number[]; positive: boolean }> = ({ data, positive }) => (
    <ResponsiveContainer width={72} height={34}>
        <LineChart data={data.map((v, i) => ({ v, i }))}>
            <Line type="monotone" dataKey="v" stroke={positive ? B.green : B.red}
                  strokeWidth={1.5} dot={false} />
        </LineChart>
    </ResponsiveContainer>
);

/* ─── Component ─────────────────────────────────────────────────── */
const InvestPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialTab = (searchParams.get('tab') as 'overview' | 'funds' | 'stocks') || 'overview';
    const [tab, setTab] = useState<'overview' | 'funds' | 'stocks'>(initialTab);
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';
    const [sectorFilter, setSectorFilter] = useState('All');

    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);
    const glass      = { background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none' };

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 12, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══════════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-76px', sm: 0 }, pt: { xs: '80px', sm: '28px' }, pb: { xs: 4, sm: 14 },
                px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -60, right: -40, width: 220, height: 220, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.gold, .14)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.green, .09)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>NG Credit</Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.15rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            Investments
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .5, background: alpha(B.green, .12), border: `1px solid ${alpha(B.green, .28)}`, borderRadius: 20, px: 1.5, py: .6 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: B.green, boxShadow: `0 0 6px ${B.green}` }} />
                        <Typography sx={{ color: B.green, fontSize: '.66rem', fontWeight: 700, letterSpacing: '.04em' }}>NGX LIVE</Typography>
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Portfolio Card ── */}
                <Box sx={{ ...glass, p: 2.5, mb: 2, border: `1px solid ${alpha(B.green, .18)}` }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.25}>
                        <Box>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 600, mb: .5 }}>
                                Portfolio Value
                            </Typography>
                            <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '2.1rem', lineHeight: 1 }}>
                                {fmt(PORTFOLIO.totalValue)}
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .5, background: alpha(B.green, .12), border: `1px solid ${alpha(B.green, .22)}`, borderRadius: 20, px: 1.25, py: .4, mb: .5 }}>
                                <NorthEastOutlined sx={{ color: B.green, fontSize: 12 }} />
                                <Typography sx={{ color: B.green, fontSize: '.72rem', fontWeight: 700 }}>+{PORTFOLIO.gainPct}%</Typography>
                            </Box>
                            <Typography sx={{ color: B.green, fontSize: '.78rem', fontWeight: 600, display: 'block' }}>+{fmt(PORTFOLIO.totalGain)}</Typography>
                            <Typography sx={{ color: textFaint, fontSize: '.62rem' }}>All-time returns</Typography>
                        </Box>
                    </Box>

                    {/* Holdings list */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: .85 }}>
                        {PORTFOLIO.holdings.map(h => (
                            <Box key={h.name} display="flex" alignItems="center" gap={1.5}>
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: h.color, flexShrink: 0 }} />
                                <Typography sx={{ color: textMid, fontSize: '.75rem', flex: 1 }}>{h.name}</Typography>
                                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.78rem' }}>{fmt(h.value)}</Typography>
                                <Typography sx={{ color: B.green, fontSize: '.7rem', fontWeight: 700, minWidth: 44, textAlign: 'right' }}>
                                    +{h.gain}%
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Divider sx={{ my: 2, borderColor: dividerC }} />

                    <Box display="flex" gap={1}>
                        <Button fullWidth variant="contained" size="small"
                                onClick={() => navigate('/fund')}
                                sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: '11px', textTransform: 'none', fontWeight: 700, fontSize: '.8rem', py: 1.1, boxShadow: 'none', '&:hover': { boxShadow: `0 6px 18px ${alpha(B.purple, .35)}` } }}>
                            Add Funds
                        </Button>
                        <Button fullWidth size="small"
                                onClick={() => navigate('/withdraw')}
                                sx={{ border: `1px solid ${dividerC}`, borderRadius: '11px', textTransform: 'none', fontWeight: 600, fontSize: '.8rem', py: 1.1, color: textMid, '&:hover': { background: alpha(B.mid, .06) } }}>
                            Withdraw
                        </Button>
                    </Box>
                </Box>

                {/* ── Tab strip ── */}
                <Box sx={{ ...glass, p: .7, mb: 2.5, display: 'flex', gap: .5 }}>
                    {([
                        { key: 'overview', label: 'Overview',     Icon: PieChartOutlined  },
                        { key: 'funds',    label: 'Mutual Funds', Icon: AutoGraphOutlined },
                        { key: 'stocks',   label: 'NGX Stocks',   Icon: ShowChartOutlined },
                    ] as const).map(({ key, label, Icon }) => {
                        const active = tab === key;
                        return (
                            <Box key={key} onClick={() => setTab(key)} sx={{
                                flex: 1, py: 1.1, borderRadius: '14px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .6,
                                background: active ? `linear-gradient(135deg, ${B.purple}, ${B.light})` : 'transparent',
                                boxShadow: active ? `0 4px 16px ${alpha(B.purple, .32)}` : 'none',
                                transition: 'all .22s',
                            }}>
                                <Icon sx={{ fontSize: 14, color: active ? '#fff' : textFaint }} />
                                <Typography sx={{ color: active ? '#fff' : textFaint, fontWeight: 700, fontSize: '.72rem' }}>
                                    {label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                {/* ════════════════════════════════════════════════════
                    OVERVIEW TAB
                ════════════════════════════════════════════════════ */}
                {tab === 'overview' && (
                    <>
                        {/* Return stats strip */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25, mb: 2 }}>
                            {[
                                { label: 'Today',   value: '+₦1,240',  up: true },
                                { label: '1 Month', value: '+₦8,420',  up: true },
                                { label: '1 Year',  value: '+₦34,820', up: true },
                            ].map(s => (
                                <Box key={s.label} sx={{ ...glass, p: 1.75, textAlign: 'center' }}>
                                    <Typography sx={{ color: B.green, fontFamily: "'Instrument Serif', serif", fontSize: '1rem', lineHeight: 1.2 }}>{s.value}</Typography>
                                    <Typography sx={{ color: textFaint, fontSize: '.62rem', mt: .3 }}>{s.label}</Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Allocation */}
                        <Box sx={{ ...glass, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.9rem', letterSpacing: '-.02em', mb: 2 }}>Allocation</Typography>
                            {[
                                { label: 'Money Market', pct: 42, color: B.green  },
                                { label: 'Equities',     pct: 30, color: B.purple },
                                { label: 'Stocks',       pct: 28, color: B.blue   },
                            ].map(a => (
                                <Box key={a.label} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
                                    <Box display="flex" justifyContent="space-between" mb={.75}>
                                        <Typography sx={{ color: textMid, fontSize: '.78rem' }}>{a.label}</Typography>
                                        <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.78rem' }}>{a.pct}%</Typography>
                                    </Box>
                                    <LinearProgress variant="determinate" value={a.pct} sx={{
                                        height: 6, borderRadius: 4,
                                        background: alpha(a.color, .1),
                                        '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${a.color}, ${alpha(a.color, .55)})`, borderRadius: 4, transition: 'transform 1s cubic-bezier(.16,1,.3,1)' }
                                    }} />
                                </Box>
                            ))}
                        </Box>

                        {/* Quick access — Invest + Trade */}
                        <Typography sx={{ color: textMid, fontSize: '.68rem', letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 600, mb: 1.25 }}>Invest & Trade</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
                            {[
                                { label: 'Mutual Funds', sub: 'From 11.9% p.a.',    icon: AutoGraphOutlined,  color: B.green,  action: () => setTab('funds')             },
                                { label: 'NGX Stocks',   sub: 'Buy individual stocks', icon: ShowChartOutlined, color: B.blue,   action: () => setTab('stocks')            },
                                { label: 'Savings',      sub: 'Up to 14.5% p.a.',   icon: SavingsOutlined,    color: B.gold,   action: () => navigate('/savings')         },
                                { label: 'Auto-Invest',  sub: 'Recurring plans',     icon: RepeatOutlined,     color: B.purple, action: () => navigate('/invest/plans')    },
                            ].map(({ label, sub, icon: Icon, color, action }) => (
                                <Box key={label} onClick={action} sx={{
                                    ...glass, p: 2, cursor: 'pointer', transition: 'all .18s',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 12px 32px ${alpha(color, .14)}` },
                                }}>
                                    <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: alpha(color, .12), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.25 }}>
                                        <Icon sx={{ color, fontSize: 20 }} />
                                    </Box>
                                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.85rem' }}>{label}</Typography>
                                    <Typography sx={{ color: textFaint, fontSize: '.7rem', mt: .3 }}>{sub}</Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Quick access — Insights */}
                        <Typography sx={{ color: textMid, fontSize: '.68rem', letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 600, mb: 1.25 }}>Insights</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                            {[
                                { label: 'Watchlist',   sub: 'Track prices',       icon: BookmarkBorderOutlined, color: B.orange, action: () => navigate('/invest/watchlist')  },
                                { label: 'Analytics',   sub: 'Portfolio deep-dive', icon: BarChartOutlined,       color: B.light,  action: () => navigate('/invest/analytics')  },
                            ].map(({ label, sub, icon: Icon, color, action }) => (
                                <Box key={label} onClick={action} sx={{
                                    ...glass, p: 2, cursor: 'pointer', transition: 'all .18s',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 12px 32px ${alpha(color, .14)}` },
                                }}>
                                    <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: alpha(color, .12), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.25 }}>
                                        <Icon sx={{ color, fontSize: 20 }} />
                                    </Box>
                                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.85rem' }}>{label}</Typography>
                                    <Typography sx={{ color: textFaint, fontSize: '.7rem', mt: .3 }}>{sub}</Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* NGX index ticker */}
                        <Box sx={{ ...glass, p: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.12em', textTransform: 'uppercase', mb: .25 }}>NGX All-Share Index</Typography>
                                <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.2rem' }}>97,842.14</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .4, background: alpha(B.green, .1), borderRadius: 20, px: 1, py: .35 }}>
                                    <NorthEastOutlined sx={{ color: B.green, fontSize: 12 }} />
                                    <Typography sx={{ color: B.green, fontWeight: 700, fontSize: '.72rem' }}>+1.2% today</Typography>
                                </Box>
                                <Typography sx={{ color: textFaint, fontSize: '.62rem', mt: .25, display: 'block' }}>Vol: ₦4.82B</Typography>
                            </Box>
                        </Box>
                    </>
                )}

                {/* ════════════════════════════════════════════════════
                    MUTUAL FUNDS TAB
                ════════════════════════════════════════════════════ */}
                {tab === 'funds' && (
                    <>
                        {/* Risk legend */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            {[
                                { label: 'Low',    color: B.green  },
                                { label: 'Medium', color: B.orange },
                                { label: 'High',   color: B.red    },
                            ].map(r => (
                                <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', gap: .5, background: alpha(r.color, .08), border: `1px solid ${alpha(r.color, .2)}`, borderRadius: 20, px: 1.25, py: .4 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: r.color }} />
                                    <Typography sx={{ color: r.color, fontSize: '.65rem', fontWeight: 600 }}>{r.label} Risk</Typography>
                                </Box>
                            ))}
                        </Box>

                        {MUTUAL_FUNDS.map(fund => (
                            <Box key={fund.id}
                                 onClick={() => navigate(`/invest/fund/${fund.id}`)}
                                 sx={{
                                     ...glass, p: 2.5, mb: 1.5, cursor: 'pointer',
                                     transition: 'all .18s',
                                     '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 14px 36px ${alpha(fund.color, .15)}` },
                                 }}>

                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                                    <Box flex={1} pr={1}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mb: .6 }}>
                                            <Box sx={{ px: 1, py: .3, borderRadius: 8, background: alpha(fund.riskColor, .1), border: `1px solid ${alpha(fund.riskColor, .22)}` }}>
                                                <Typography sx={{ color: fund.riskColor, fontSize: '.58rem', fontWeight: 700 }}>{fund.risk}</Typography>
                                            </Box>
                                            <Typography sx={{ color: textFaint, fontSize: '.65rem' }}>{fund.category}</Typography>
                                        </Box>
                                        <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.88rem', lineHeight: 1.3, letterSpacing: '-.015em' }}>
                                            {fund.name}
                                        </Typography>
                                        <Typography sx={{ color: textFaint, fontSize: '.68rem', mt: .3 }}>
                                            {fund.manager} · AUM {fund.aum}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                        <Typography sx={{ color: B.green, fontFamily: "'Instrument Serif', serif", fontSize: '1.4rem', lineHeight: 1 }}>
                                            {fund.returns.yr1}%
                                        </Typography>
                                        <Typography sx={{ color: textFaint, fontSize: '.6rem' }}>1yr returns</Typography>
                                    </Box>
                                </Box>

                                <Box display="flex" justifyContent="space-between" alignItems="flex-end">
                                    <Box display="flex" gap={2.5}>
                                        {[
                                            { label: 'YTD',       value: `${fund.returns.ytd}%`   },
                                            { label: 'Min. invest', value: `₦${(fund.minInvestment/1000).toFixed(0)}k` },
                                            { label: 'NAV',       value: `₦${fund.nav}`           },
                                        ].map(({ label, value }) => (
                                            <Box key={label}>
                                                <Typography sx={{ color: textFaint, fontSize: '.58rem', letterSpacing: '.06em', textTransform: 'uppercase' }}>{label}</Typography>
                                                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.8rem', mt: .2 }}>{value}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                    <MiniChart data={fund.chart} positive={fund.returns.d1 > 0} />
                                </Box>

                                {/* colour accent bar */}
                                <Box sx={{ mt: 1.75, height: 2.5, borderRadius: 4, background: `linear-gradient(90deg, ${fund.color}, ${alpha(fund.color, .15)})` }} />
                            </Box>
                        ))}
                    </>
                )}

                {/* ════════════════════════════════════════════════════
                    STOCKS TAB
                ════════════════════════════════════════════════════ */}
                {tab === 'stocks' && (
                    <>
                        {/* Index bar */}
                        <Box sx={{ ...glass, p: 1.75, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.1em', textTransform: 'uppercase', mb: .2 }}>NGX All-Share Index</Typography>
                                <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.15rem' }}>97,842.14</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
                                <NorthEastOutlined sx={{ color: B.green, fontSize: 14 }} />
                                <Typography sx={{ color: B.green, fontWeight: 700, fontSize: '.8rem' }}>+1.2%</Typography>
                                <Typography sx={{ color: textFaint, fontSize: '.72rem' }}>today</Typography>
                            </Box>
                        </Box>

                        {/* Sector filters */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, overflowX: 'auto', pb: .5, '&::-webkit-scrollbar': { display: 'none' } }}>
                            {['All', 'Banking', 'Telecoms', 'Oil & Gas', 'Building Materials'].map((s) => (
                                <Box key={s} onClick={() => setSectorFilter(s)} sx={{
                                    px: 1.5, py: .55, borderRadius: 20, flexShrink: 0, cursor: 'pointer',
                                    background: sectorFilter === s ? `linear-gradient(135deg, ${B.purple}, ${B.light})` : alpha(isDark ? '#fff' : B.dark, .05),
                                    border: `1px solid ${sectorFilter === s ? 'transparent' : alpha(isDark ? '#fff' : B.dark, .08)}`,
                                    transition: 'all .18s',
                                }}>
                                    <Typography sx={{ color: sectorFilter === s ? '#fff' : textMid, fontSize: '.7rem', fontWeight: 600 }}>{s}</Typography>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ ...glass, overflow: 'hidden' }}>
                            {NGX_STOCKS.filter(s => sectorFilter === 'All' || s.sector === sectorFilter).map((stock, i) => (
                                <React.Fragment key={stock.id}>
                                    <Box
                                        onClick={() => navigate(`/invest/stock/${stock.id}`)}
                                        sx={{
                                            px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 2,
                                            cursor: 'pointer', transition: 'background .15s',
                                            '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .025) },
                                        }}>
                                        {/* Ticker badge */}
                                        <Box sx={{
                                            width: 46, height: 46, borderRadius: '13px', flexShrink: 0,
                                            background: alpha(stock.color, .1),
                                            border: `1px solid ${alpha(stock.color, .2)}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <Typography sx={{ color: stock.color, fontWeight: 800, fontSize: stock.ticker.length > 6 ? '.5rem' : '.6rem', letterSpacing: '.01em', textAlign: 'center', lineHeight: 1.2 }}>
                                                {stock.ticker.slice(0, 6)}
                                            </Typography>
                                        </Box>

                                        <Box flex={1} minWidth={0}>
                                            <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.84rem', letterSpacing: '-.015em' }}>
                                                {stock.name}
                                            </Typography>
                                            <Typography sx={{ color: textFaint, fontSize: '.68rem' }}>{stock.sector}</Typography>
                                        </Box>

                                        <MiniChart data={stock.chart} positive={stock.change > 0} />

                                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                            <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.86rem' }}>
                                                ₦{stock.price.toLocaleString()}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: .3 }}>
                                                {stock.change > 0
                                                    ? <TrendingUpOutlined sx={{ color: B.green, fontSize: 12 }} />
                                                    : <TrendingDownOutlined sx={{ color: B.red, fontSize: 12 }} />
                                                }
                                                <Typography sx={{ color: stock.change > 0 ? B.green : B.red, fontSize: '.72rem', fontWeight: 700 }}>
                                                    {stock.change > 0 ? '+' : ''}{stock.change}%
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    {i < NGX_STOCKS.filter(s => sectorFilter === 'All' || s.sector === sectorFilter).length - 1 && <Divider sx={{ mx: 2.5, borderColor: dividerC }} />}
                                </React.Fragment>
                            ))}
                        </Box>
                    </>
                )}

                {/* Footer */}
                <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: .6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .75 }}>
                        <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                        <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                            256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '.6rem', color: alpha(isDark ? '#fff' : B.ink, .14), textAlign: 'center' }}>
                        Investments carry risk. Past returns do not guarantee future performance. SEC licensed.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default InvestPage;